/**
 * A searchable, grouped picker for the "Maps to" columns.
 *
 * Replaces a plain Radix Select, which had two problems on real data: with 47
 * metric definitions the list ran past the bottom of the window with no way to
 * reach the rest, and every option had to be found by eye.
 *
 * Grouping is the other half. Targets already claimed by another row are almost
 * never the right answer, so they are pushed below the ones still unused — the
 * list a user actually needs to read is the short one at the top.
 */
import { useMemo, useState } from "react";

import { Check, ChevronDown } from "lucide-react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

export type SelectOption = {
  id: string;
  /** What the user reads. Must not carry status text — that belongs in a column. */
  label: string;
  /** Extra text matched by the search box but not displayed. */
  keywords?: string;
};

export type OptionGroup = {
  heading: string;
  options: SelectOption[];
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** Always first, ungrouped — the "skip"/"keep" escape hatch. */
  leadingOption: SelectOption;
  groups: OptionGroup[];
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  /** Accessible name, since the trigger's text changes with the selection. */
  ariaLabel?: string;
};

export function SearchableSelect({
  value,
  onChange,
  leadingOption,
  groups,
  searchPlaceholder = "Search…",
  className,
  disabled,
  ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);

  const selectedLabel = useMemo(() => {
    if (value === leadingOption.id) return leadingOption.label;
    for (const g of groups) {
      const hit = g.options.find(o => o.id === value);
      if (hit) return hit.label;
    }
    return leadingOption.label;
  }, [value, groups, leadingOption]);

  const choose = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  const renderItem = (o: SelectOption) => (
    <CommandItem
      key={o.id}
      // cmdk filters on `value`; include the id and any keywords so a search
      // for "arr" finds "Annual Recurring Revenue".
      value={`${o.label} ${o.id} ${o.keywords ?? ""}`}
      onSelect={() => choose(o.id)}
    >
      <Check
        className={cn(
          "h-4 w-4 shrink-0",
          value === o.id ? "opacity-100" : "opacity-0"
        )}
      />
      <span className="truncate">{o.label}</span>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "flex h-8 w-[250px] items-center justify-between gap-1.5 rounded-md border border-gray-300",
            "bg-white px-2.5 text-left text-sm text-gray-900",
            "hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-400",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[320px] p-0"
        // Without this the list can render taller than the viewport on a short
        // window, which is the bug this component exists to fix.
        collisionPadding={12}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>{renderItem(leadingOption)}</CommandGroup>
            {groups
              .filter(g => g.options.length > 0)
              .map(g => (
                <div key={g.heading}>
                  <CommandSeparator />
                  <CommandGroup heading={`${g.heading} (${g.options.length})`}>
                    {g.options.map(renderItem)}
                  </CommandGroup>
                </div>
              ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
